import Dropzone from 'react-dropzone';
import {
    DropzoneCustomHeightWrap,
    DropzoneHere,
    DropzoneImage,
    DropzoneImageDelete,
    DropzoneImageName,
    DropzoneInput,
    DropzoneSingleWrap,
} from './DropzoneElements';
import { useEffect, useState } from 'react';
import { mediaService, useMedia } from '@/services/media';
import { LoadingSpinner } from '../../Loading';
import { MediaTypes } from '@/constants';
// import ReactPlayer from 'react-player';
import MuxPlayer from '@mux/mux-player-react/lazy';
import { uploadFile, uploadPermanentFile, useFile } from '@/services/files';

interface DropZoneFieldProps {
    customHeight?: boolean;
    accept: string;
    currentValue: string;
    currentFileType: string;
    onChange: (value: string, type: MediaTypes, file?: File) => void;
    description?: string;
    // When true, images upload via /files/permanent and onChange receives the
    // permanent URL (not a fileId). Use for assets baked verbatim into HTML
    // (e.g. blog cover/OG images) where a 1h signed URL would break.
    usePermanentUrl?: boolean;
    // Storage folder for image uploads (default 'contents/images').
    uploadPath?: string;
    // Fired true while an upload is in flight, false when it finishes (success
    // or failure). Lets a parent form block "Save" until media is fully ready.
    onUploadingChange?: (uploading: boolean) => void;
}

const DropZoneField = ({ customHeight, accept, currentValue, currentFileType, onChange, description, usePermanentUrl, uploadPath = 'contents/images', onUploadingChange }: DropZoneFieldProps) => {
    const [files, setFiles] = useState<File[]>([]);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [fileId, setFileId] = useState('');
    const [currentFilePreview, setCurrentFilePreview] = useState('');
    const [mediaId, setMediaId] = useState('');
    const [mediaLink, setMediaLink] = useState('');
    const [preview, setPreview] = useState<JSX.Element>();

    const { data: fileResponse } = useFile(fileId);
    const { data: mediaResponse } = useMedia(mediaId);

    useEffect(() => {
        if (mediaResponse?.data) {
            const { playback_ids } = mediaResponse.data;
            setMediaLink(`https://stream.mux.com/${playback_ids[0].id}.m3u8`);
        }
    }, [mediaResponse]);

    useEffect(() => {
        if (fileResponse?.data) {
            setCurrentFilePreview(fileResponse.data.url);
        }
    }, [fileResponse]);

    useEffect(() => {
        if (currentValue) {
            const fileType = files[0]?.type || currentFileType;
            if (fileType.includes('image')) {
                if (currentValue.startsWith('http')) {
                    setCurrentFilePreview(currentValue);
                } else {
                    setFileId(currentValue);
                }
            } else {
                if (currentValue.startsWith('http')) {
                    setMediaLink(currentValue);
                } else {
                    setMediaId(currentValue);
                }
            }
        } else {
            setCurrentFilePreview('');
            setFileId('');
            setMediaId('');
            setMediaLink('');
        }
    }, [currentValue]);

    const onDrop = async (acceptedFiles: File[], rejectedFiles: any[]) => {
        if (acceptedFiles.length === 0) {
            console.error('No files selected', rejectedFiles);
            return;
        }

        try {
            setLoading(true);
            onUploadingChange?.(true);
            setUploadProgress(0);

            const acceptedFile = acceptedFiles[0];
            const fileType = acceptedFile.type;
            setFiles(acceptedFiles);

            if (fileType.includes('image')) {
                const base64String = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result as string);
                    reader.onerror = reject;
                    reader.readAsDataURL(acceptedFile);
                });
                const name = acceptedFile.name;
                const path = uploadPath;
                const file = base64String;
                if (usePermanentUrl) {
                    // Permanent (non-expiring) URL — onChange receives the URL itself,
                    // which the caller stores verbatim (it gets baked into static HTML).
                    const response = await uploadPermanentFile({ file, path, name, contentType: fileType });
                    if (response.data) {
                        setCurrentFilePreview(response.data.url);
                        onChange(response.data.url, '' as MediaTypes);
                    }
                } else {
                    const response = await uploadFile({ file, path, name, contentType: fileType });
                    if (response.data) {
                        setCurrentFilePreview(response.data.url);
                        setFileId(response.data.fileId);
                        onChange(response.data.fileId, '' as MediaTypes);
                    }
                }
            } else {
                const response = await mediaService.uploadFileChunked(
                    acceptedFile,
                    {
                        name: acceptedFile.name,
                        description: description || `Uploaded via admin web interface at ${new Date().toISOString()}`,
                    },
                    setUploadProgress,
                );
                const id = response?.uri as string;

                setMediaId(id);
                const mediaType = fileType.startsWith('audio/') ? MediaTypes.Audio : MediaTypes.Video;
                onChange(id, mediaType, acceptedFile);
            }
        } catch (error) {
            console.error('Error uploading file:', error);
            // Clear the dropped file + any stale value so we don't leave a
            // misleading empty preview (which looks uploaded but has no id).
            setFiles([]);
            onChange('', '' as MediaTypes);
            window.alert(error instanceof Error ? error.message : 'Upload failed. Please try again.');
        } finally {
            setLoading(false);
            onUploadingChange?.(false);
            setTimeout(() => setUploadProgress(0), 1000);
        }
    };

    const removeFile = (e: React.MouseEvent) => {
        e.preventDefault();
        setFiles([]);
        onChange('', '' as MediaTypes);
        setMediaLink('');
        setCurrentFilePreview('');
        setFileId('');
        setMediaId('');
    };

    const renderPreview = () => {
        if ((!files || !files.length) && !mediaLink && !currentFilePreview) {
            return setPreview(<p>No file selected.</p>);
        }

        const fileType = files[0]?.type || currentFileType;

        switch (true) {
            case fileType.includes('image'):
                return setPreview(<img src={currentFilePreview} alt="preview-img" />);
            case fileType.includes('audio'):
            case fileType.includes('video'):
                const parts = mediaLink.split('/');
                const lastPart = parts[parts.length - 1];
                const playbackId = lastPart.split('.')[0];

                return setPreview(
                    <MuxPlayer
                        loading="viewport"
                        playbackId={playbackId}
                        audio={fileType.includes('audio')}
                        style={{ height: fileType.includes('audio') ? '60px' : '100%' }}
                    />,
                );
            default:
                return setPreview(<p>Unsupported file type</p>);
        }
    };

    useEffect(() => {
        renderPreview();
    }, [currentFileType, files?.[0]?.name, currentFilePreview, mediaLink]);

    const DropzoneWrap = customHeight ? DropzoneCustomHeightWrap : DropzoneSingleWrap;

    return (
        <DropzoneWrap>
            {loading ? (
                <div className="w-100 d-flex justify-content-center align-items-center">
                    <LoadingSpinner />
                    {uploadProgress > 0 && <div>Uploading: {uploadProgress}%</div>}
                </div>
            ) : (
                <>
                    <Dropzone accept={accept} multiple={false} onDrop={onDrop}>
                        {({ getRootProps, getInputProps }) => (
                            <DropzoneInput {...getRootProps()}>
                                {(!files || files.length === 0) && (
                                    <DropzoneHere>
                                        <span className="lnr lnr-upload" /> Drop file here to upload
                                    </DropzoneHere>
                                )}
                                <input {...getInputProps()} />
                            </DropzoneInput>
                        )}
                    </Dropzone>
                    {(currentFilePreview || mediaLink || (files && Array.isArray(files) && files.length > 0)) && (
                        <DropzoneImage>
                            {preview}
                            <DropzoneImageName>{files[0]?.name}</DropzoneImageName>
                            <DropzoneImageDelete type="button" onClick={removeFile}>
                                Remove
                            </DropzoneImageDelete>
                        </DropzoneImage>
                    )}
                </>
            )}
        </DropzoneWrap>
    );
};

export default DropZoneField;
